// src/store/authSlice.ts
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import API_URLS from "~/utils/urls.ts";
import API, {ShopifyAPI} from "~/utils/api.ts";
import {
  AuthenticateResponse,
  AuthState,
  ShopifyAuthenticateParams,
  ShopifyInstallUrlResponse,
  ShopifySession,
  ShopifyVerifiedResponse
} from "~/types/auth";

const initialState: AuthState = {
  isAuthenticated: false,
  shop: null,
  accessToken: null,
  loading: false,
  error: null,
};

export const verifyAuthenticatedUser = createAsyncThunk(
    'auth/verifyAuthenticatedUser',
    async (session: ShopifySession, {rejectWithValue}) => {
      if(!session.shop || !session.hmac || !session.public_id || !session.shopifyAdminUrl) {
        return rejectWithValue('Session expired, please log in again.');
      }

        try {
            const response = await ShopifyAPI.get(API_URLS.AUTH.VERIFY(session.public_id));
            return response.data as ShopifyVerifiedResponse;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const authenticateShopify = createAsyncThunk(
    'auth/authenticateShopify',
    async ({
             public_id,
             params
           }: ShopifyAuthenticateParams, { rejectWithValue }) => {
      try {
        const stored_state = localStorage.getItem('state');
        const response = await ShopifyAPI.post(API_URLS.AUTH.CALLBACK(public_id), {
          params,
          stored_state
        });

        return response.data as AuthenticateResponse;
      } catch (error: any) {
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
        return rejectWithValue('Authentication failed');
      }
    }
);

export const fetchShopifyInstallUrl = createAsyncThunk(
    'auth/fetchShopifyInstallUrl',
    async ({public_id, shop}: {public_id: string; shop:string;}, {rejectWithValue}) => {
        try {
          const url = `${API_URLS.AUTH.INSTALL}?pid=${public_id}&shop=${shop}`;
          const response = await API.get(url);

          return response.data as ShopifyInstallUrlResponse;
        } catch (error: any) {
          return rejectWithValue(error.response.data);
        }
    }
);

interface ErrorPayload {
  detail?: string;
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.shop = null;
      state.accessToken = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticateShopify.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authenticateShopify.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = true;
      })
      .addCase(authenticateShopify.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
        .addCase(fetchShopifyInstallUrl.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(fetchShopifyInstallUrl.fulfilled, (state) => {
            state.loading = false;
        })
        .addCase(fetchShopifyInstallUrl.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        })
        .addCase(verifyAuthenticatedUser.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(verifyAuthenticatedUser.fulfilled, (state) => {
            state.loading = false;
            state.isAuthenticated = true;
        })
        .addCase(verifyAuthenticatedUser.rejected, (state, action) => {
            state.loading = false;
            if(typeof action?.payload === 'object') {
                state.error = (action.payload as ErrorPayload).detail || 'An error occurred';
                return;
            } else {
                state.error = action.payload as string;
            }
        });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;